import { vehicleSelectOptions } from "../../constants/vehicleSelectOptions";
import SearchableSelect from "./SearchableSelect";

export const emptyInventoryFilters = {
  query: "",
  make: "",
  model: "",
  trim: "",
  bodyType: "",
  condition: "",
  minYear: "",
  maxYear: "",
  fuel: "",
  drivetrain: "",
  engine: "",
  status: "",
  exteriorColor: "",
  interiorColor: "",
  transmission: "",
  minPrice: "",
  maxPrice: "",
  minMileage: "",
  maxMileage: "",
};

export const inventoryFilterKeys = Object.keys(emptyInventoryFilters);

export const inventoryFiltersFromSearchParams = (searchParams) =>
  inventoryFilterKeys.reduce((filters, key) => ({
    ...filters,
    [key]: searchParams.get(key) || "",
  }), { ...emptyInventoryFilters });

export const syncInventoryFiltersToSearchParams = (searchParams, filters, options = {}) => {
  const nextSearchParams = new URLSearchParams(searchParams);
  inventoryFilterKeys.forEach((key) => {
    const value = String(filters[key] || "").trim();
    if (value) {
      nextSearchParams.set(key, value);
    } else {
      nextSearchParams.delete(key);
    }
  });
  (options.deleteKeys || []).forEach((key) => nextSearchParams.delete(key));
  return nextSearchParams;
};

const filterOptionsFor = (filterOptions, key) =>
  filterOptions?.[key] || [];

const cleanSelectOptionValue = (value) =>
  String(value || "").replace(/\s*[[(][^\])]*[\])]\s*$/g, "").trim();

const statusOptionValue = (value) => {
  const cleaned = cleanSelectOptionValue(value);
  return cleaned.toLowerCase() === "ready" ? "Available" : cleaned;
};
const statusOptionToken = (value) =>
  statusOptionValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const hiddenFilterStatusTokens = new Set([
  "awaiting_photos",
  "awaiting_title",
  "in_inspection",
  "in_reconditioning",
  "on_hold",
  "removed",
  "reserved",
  "search_inventory",
  "sold",
]);
const shouldShowStatusFilter = (value) =>
  !hiddenFilterStatusTokens.has(statusOptionToken(value));

const mergedOptionsFor = (filterOptions, key, baseOptions = []) =>
  [...new Set([
    ...baseOptions.map(cleanSelectOptionValue),
    ...filterOptionsFor(filterOptions, key).map(cleanSelectOptionValue),
  ].filter(Boolean))];

const statusOptionsFor = (filterOptions) =>
  [...new Set([
    "Newest Cars",
    ...vehicleSelectOptions.statuses.map(statusOptionValue),
    "Used",
    "New",
    "Deals",
    ...filterOptionsFor(filterOptions, "statuses").map(statusOptionValue),
  ].filter(Boolean))]
    .filter(shouldShowStatusFilter);

const FILTER_RANGE_MIN = 0;
const PRICE_RANGE_MAX = 200000;
const MILEAGE_RANGE_MAX = 500000;
const DEFAULT_MIN_PRICE = 500;
const DEFAULT_MAX_PRICE = 150000;
const DEFAULT_MIN_MILEAGE = 10;
const DEFAULT_MAX_MILEAGE = 300000;
const clampRangeValue = (value, min, max) =>
  Math.min(Math.max(Number(value) || min, min), max);
const numericInputValue = (value) =>
  Number(String(value || "").replace(/[^\d.]/g, ""));
const formattedRangeValue = (value) =>
  Number(value || 0).toLocaleString("en-US");

export function InventoryFilterPanel({
  activeMinMileage = 0,
  activeMaxMileage,
  activeMinPrice = 0,
  activeMaxPrice,
  filterOptions,
  filters,
  hasActiveFilters,
  matchCount,
  onReset,
  onUpdateFilter,
  priceLabel,
  statusFilterOptions = [],
}) {
  const updateFilter = (field, value) => onUpdateFilter(field, value);
  const activeStatusValue = filters.status ? statusOptionToken(filters.status) : "";
  const priceRangeMax = PRICE_RANGE_MAX;
  const mileageRangeMax = MILEAGE_RANGE_MAX;
  const activeMinPriceValue = filters.minPrice ? clampRangeValue(activeMinPrice, FILTER_RANGE_MIN, priceRangeMax) : DEFAULT_MIN_PRICE;
  const activeMaxPriceValue = filters.maxPrice ? clampRangeValue(activeMaxPrice, FILTER_RANGE_MIN, priceRangeMax) : DEFAULT_MAX_PRICE;
  const activeMinMileageValue = filters.minMileage ? clampRangeValue(activeMinMileage, FILTER_RANGE_MIN, mileageRangeMax) : DEFAULT_MIN_MILEAGE;
  const activeMaxMileageValue = filters.maxMileage ? clampRangeValue(activeMaxMileage, FILTER_RANGE_MIN, mileageRangeMax) : DEFAULT_MAX_MILEAGE;
  const updateRangeFilter = (field, value, boundary, emptyValue) => {
    const nextValue = clampRangeValue(value, FILTER_RANGE_MIN, boundary);
    updateFilter(field, nextValue === emptyValue ? "" : String(nextValue));
  };
  const rangeTrackStyle = (minValue, maxValue, boundary) => ({
    "--range-start": `${(minValue / boundary) * 100}%`,
    "--range-end": `${(maxValue / boundary) * 100}%`,
  });
  const sidebarStatusOptions = statusFilterOptions.length
    ? statusFilterOptions
    : [
      ...statusOptionsFor(filterOptions).map((option) => ({ label: option, value: option })),
    ];
  const renderFilterSelect = ({ ariaLabel, customPlaceholder, field, options, placeholder, value = filters[field] }) => (
    <SearchableSelect
      ariaLabel={ariaLabel}
      className="auto-filter-searchable-select"
      customPlaceholder={customPlaceholder}
      onChange={(nextValue) => updateFilter(field, nextValue)}
      options={options}
      placeholder={placeholder}
      value={cleanSelectOptionValue(value)}
    />
  );

  return (
    <aside className="auto-inventory-filter-sidebar" aria-label="Inventory filters">
      <div className="auto-filter-head">
        <span className="tiny-icon"><i className="ti ti-adjustments-horizontal" /></span>
        <div>
          <strong>Filters</strong>
          <small>{hasActiveFilters ? `${matchCount} matches` : "All vehicles"}</small>
        </div>
        <button className="auto-filter-reset-icon" type="button" onClick={onReset} aria-label="Reset filters">
          <i className="ti ti-filter-off" />
          <span>Reset</span>
        </button>
      </div>
      <div className="auto-filter-field auto-filter-status-field">
        <span>Status</span>
        <div className="auto-filter-status-options">
          {sidebarStatusOptions.map((option) => (
            <button
              className={activeStatusValue === statusOptionToken(option.value) ? "is-active" : ""}
              key={option.label}
              type="button"
              onClick={() => updateFilter("status", option.value)}
            >
              <span>{option.label}</span>
              {Number.isFinite(option.count) ? <b>{option.count}</b> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="auto-filter-field">
        <span>Price range <b>{priceLabel(activeMinPriceValue)} - {priceLabel(activeMaxPriceValue)}</b></span>
        <div className="auto-range-input-pair">
          <input
            type="text"
            inputMode="numeric"
            value={formattedRangeValue(activeMinPriceValue)}
            onChange={(event) => updateRangeFilter("minPrice", Math.min(numericInputValue(event.target.value), activeMaxPriceValue), priceRangeMax, DEFAULT_MIN_PRICE)}
            onFocus={(event) => event.target.select()}
            aria-label="Minimum price amount"
            placeholder="Min price"
          />
          <input
            type="text"
            inputMode="numeric"
            value={formattedRangeValue(activeMaxPriceValue)}
            onChange={(event) => updateRangeFilter("maxPrice", Math.max(numericInputValue(event.target.value), activeMinPriceValue), priceRangeMax, DEFAULT_MAX_PRICE)}
            onFocus={(event) => event.target.select()}
            aria-label="Maximum price amount"
            placeholder="Max price"
          />
        </div>
        <div className="auto-range-slider" style={rangeTrackStyle(activeMinPriceValue, activeMaxPriceValue, priceRangeMax)} aria-label="Price range">
          <input
            type="range"
            min="0"
            max={priceRangeMax}
            step="500"
            value={activeMinPriceValue}
            onChange={(event) => updateRangeFilter("minPrice", Math.min(Number(event.target.value), activeMaxPriceValue), priceRangeMax, DEFAULT_MIN_PRICE)}
            aria-label="Minimum price"
          />
          <input
            type="range"
            min="0"
            max={priceRangeMax}
            step="500"
            value={activeMaxPriceValue}
            onChange={(event) => updateRangeFilter("maxPrice", Math.max(Number(event.target.value), activeMinPriceValue), priceRangeMax, DEFAULT_MAX_PRICE)}
            aria-label="Maximum price"
          />
        </div>
      </div>
      <div className="auto-filter-field">
        <span>Mileage range <b>{activeMinMileageValue.toLocaleString("en-US")} - {activeMaxMileageValue.toLocaleString("en-US")} mi</b></span>
        <div className="auto-range-input-pair">
          <input
            type="text"
            inputMode="numeric"
            value={formattedRangeValue(activeMinMileageValue)}
            onChange={(event) => updateRangeFilter("minMileage", Math.min(numericInputValue(event.target.value), activeMaxMileageValue), mileageRangeMax, DEFAULT_MIN_MILEAGE)}
            onFocus={(event) => event.target.select()}
            aria-label="Minimum mileage amount"
            placeholder="Min mileage"
          />
          <input
            type="text"
            inputMode="numeric"
            value={formattedRangeValue(activeMaxMileageValue)}
            onChange={(event) => updateRangeFilter("maxMileage", Math.max(numericInputValue(event.target.value), activeMinMileageValue), mileageRangeMax, DEFAULT_MAX_MILEAGE)}
            onFocus={(event) => event.target.select()}
            aria-label="Maximum mileage amount"
            placeholder="Max mileage"
          />
        </div>
        <div className="auto-range-slider" style={rangeTrackStyle(activeMinMileageValue, activeMaxMileageValue, mileageRangeMax)} aria-label="Mileage range">
          <input
            type="range"
            min="0"
            max={mileageRangeMax}
            step="10"
            value={activeMinMileageValue}
            onChange={(event) => updateRangeFilter("minMileage", Math.min(Number(event.target.value), activeMaxMileageValue), mileageRangeMax, DEFAULT_MIN_MILEAGE)}
            aria-label="Minimum mileage"
          />
          <input
            type="range"
            min="0"
            max={mileageRangeMax}
            step="10"
            value={activeMaxMileageValue}
            onChange={(event) => updateRangeFilter("maxMileage", Math.max(Number(event.target.value), activeMinMileageValue), mileageRangeMax, DEFAULT_MAX_MILEAGE)}
            aria-label="Maximum mileage"
          />
        </div>
      </div>
      <label className="auto-filter-field">
        <span>Make</span>
        {renderFilterSelect({
          ariaLabel: "Make",
          customPlaceholder: "Enter make",
          field: "make",
          options: mergedOptionsFor(filterOptions, "makes", vehicleSelectOptions.makes),
          placeholder: "Select make",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Model</span>
        {renderFilterSelect({
          ariaLabel: "Model",
          customPlaceholder: "Enter model",
          field: "model",
          options: mergedOptionsFor(filterOptions, "models", vehicleSelectOptions.models),
          placeholder: "Select model",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Trim</span>
        {renderFilterSelect({
          ariaLabel: "Trim",
          customPlaceholder: "Enter trim",
          field: "trim",
          options: mergedOptionsFor(filterOptions, "trims", vehicleSelectOptions.trims),
          placeholder: "Select trim",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Body style</span>
        {renderFilterSelect({
          ariaLabel: "Body style",
          customPlaceholder: "Enter body style",
          field: "bodyType",
          options: mergedOptionsFor(filterOptions, "bodyTypes", vehicleSelectOptions.bodyTypes),
          placeholder: "Select body style",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Condition</span>
        {renderFilterSelect({
          ariaLabel: "Condition",
          customPlaceholder: "Enter condition",
          field: "condition",
          options: mergedOptionsFor(filterOptions, "conditions", vehicleSelectOptions.conditions),
          placeholder: "Select condition",
        })}
      </label>
      <div className="auto-filter-field">
        <span>Year range</span>
        <div className="auto-filter-pair">
          {renderFilterSelect({
            ariaLabel: "Minimum year",
            customPlaceholder: "Enter min year",
            field: "minYear",
            options: mergedOptionsFor(filterOptions, "years", vehicleSelectOptions.years),
            placeholder: "Min year",
          })}
          {renderFilterSelect({
            ariaLabel: "Maximum year",
            customPlaceholder: "Enter max year",
            field: "maxYear",
            options: mergedOptionsFor(filterOptions, "years", vehicleSelectOptions.years),
            placeholder: "Max year",
          })}
        </div>
      </div>
      <label className="auto-filter-field">
        <span>Fuel</span>
        {renderFilterSelect({
          ariaLabel: "Fuel",
          customPlaceholder: "Enter fuel",
          field: "fuel",
          options: mergedOptionsFor(filterOptions, "fuels", vehicleSelectOptions.fuelTypes),
          placeholder: "Select fuel",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Drivetrain</span>
        {renderFilterSelect({
          ariaLabel: "Drivetrain",
          customPlaceholder: "Enter drivetrain",
          field: "drivetrain",
          options: mergedOptionsFor(filterOptions, "drivetrains", vehicleSelectOptions.drivetrains),
          placeholder: "Select drivetrain",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Engine</span>
        {renderFilterSelect({
          ariaLabel: "Engine",
          customPlaceholder: "Enter engine",
          field: "engine",
          options: mergedOptionsFor(filterOptions, "engines", vehicleSelectOptions.engines),
          placeholder: "Select engine",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Exterior color</span>
        {renderFilterSelect({
          ariaLabel: "Exterior color",
          customPlaceholder: "Enter exterior color",
          field: "exteriorColor",
          options: mergedOptionsFor(filterOptions, "exteriorColors", vehicleSelectOptions.exteriorColors),
          placeholder: "Select exterior",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Interior color</span>
        {renderFilterSelect({
          ariaLabel: "Interior color",
          customPlaceholder: "Enter interior color",
          field: "interiorColor",
          options: mergedOptionsFor(filterOptions, "interiorColors", vehicleSelectOptions.interiorColors),
          placeholder: "Select interior",
        })}
      </label>
      <label className="auto-filter-field">
        <span>Transmission</span>
        {renderFilterSelect({
          ariaLabel: "Transmission",
          customPlaceholder: "Enter transmission",
          field: "transmission",
          options: mergedOptionsFor(filterOptions, "transmissions", vehicleSelectOptions.transmissions),
          placeholder: "Select transmission",
        })}
      </label>
      <button className="btn secondary" type="button" onClick={onReset}>
        <i className="ti ti-filter-off" /> Reset
      </button>
    </aside>
  );
}
